import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useContent } from "../context/ContentContext";
import { external } from "../lib/links";
import { scrollToId } from "../lib/scroll";
import { slugify } from "../lib/slug";

export const Navbar = () => {
  const { content } = useContent();
  const nav = content.nav || [];
  const links = content.links;
  const blogEnabled = content.blogPage?.enabled !== false;
  const blogLabel = content.blogPage?.navLabel || "Blog";
  const activeAreas = (content?.treatment?.areas || []).filter((a) => a.active !== false && a.name);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openTA, setOpenTA] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const solid = scrolled || location.pathname !== "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      sessionStorage.setItem("kb_scroll", id);
      navigate("/");
    } else {
      requestAnimationFrame(() => scrollToId(id));
    }
  };

  const goTop = () => {
    if (location.pathname !== "/") navigate("/");
    else scrollToId("hero");
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 w-full z-50 transition-[background-color,box-shadow,padding] duration-500 ${
        solid
          ? "backdrop-blur-xl bg-cream/80 border-b border-sand/60 py-3 shadow-[0_8px_30px_-18px_rgba(122,143,119,0.35)]"
          : "bg-transparent py-5"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8 flex items-center justify-between">
        <button data-testid="nav-logo" onClick={goTop} className="flex items-center gap-2.5 group">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-sage/20 border border-sage/40 overflow-hidden">
            <span className="h-4 w-4 rounded-full bg-peach" />
          </span>
          <span className={`font-serif text-xl md:text-2xl tracking-tight leading-none transition-colors duration-300 ${solid ? "text-ink" : "text-white drop-shadow-[0_2px_6px_rgba(20,30,35,0.8)]"}`}>
            Karen Boley
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {nav.map((l) => {
            const isTreatment = l.id === "treatment" && activeAreas.length > 0;
            if (isTreatment) {
              return (
                <div
                  key={l.id}
                  data-testid="nav-treatment-menu"
                  className="relative"
                  onMouseEnter={() => setOpenTA(true)}
                  onMouseLeave={() => setOpenTA(false)}
                >
                  <button
                    data-testid={`nav-${l.id}`}
                    onClick={() => goSection(l.id)}
                    className={`inline-flex items-center gap-1 text-base font-bold transition-colors duration-300 relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:transition-all after:duration-300 hover:after:w-full ${
                      solid ? "text-ink-soft hover:text-sage-dark after:bg-sage-dark" : "text-white hover:text-white after:bg-white drop-shadow-[0_1px_8px_rgba(20,30,35,0.5)]"
                    }`}
                  >
                    {l.label}
                    <ChevronDown className="h-3.5 w-3.5 opacity-70" />
                  </button>
                  <AnimatePresence>
                    {openTA && (
                      <motion.div
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                        data-testid="nav-treatment-dropdown"
                        className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-64"
                      >
                        <div className="rounded-2xl bg-white/95 backdrop-blur-xl border border-sand/60 shadow-[0_20px_50px_-20px_rgba(20,30,35,0.35)] p-2">
                          {activeAreas.map((a) => {
                            const slug = a.slug || slugify(a.name);
                            return (
                              <Link
                                key={a.id || slug}
                                to={`/treatment-areas/${slug}`}
                                onClick={() => setOpenTA(false)}
                                data-testid={`nav-ta-${slug}`}
                                className="block px-3 py-2 rounded-lg text-sm text-ink hover:bg-sage/10 hover:text-sage-dark transition-colors"
                              >
                                {a.name}
                              </Link>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            }
            return (
              <button
                key={l.id}
                data-testid={`nav-${l.id}`}
                onClick={() => goSection(l.id)}
                className={`text-base font-bold transition-colors duration-300 relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:transition-all after:duration-300 hover:after:w-full ${
                  solid ? "text-ink-soft hover:text-sage-dark after:bg-sage-dark" : "text-white hover:text-white after:bg-white drop-shadow-[0_1px_8px_rgba(20,30,35,0.5)]"
                }`}
              >
                {l.label}
              </button>
            );
          })}
          {blogEnabled && (
            <Link data-testid="nav-blog" to="/blog" className={`text-base font-bold transition-colors duration-300 relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:transition-all after:duration-300 hover:after:w-full ${
              solid ? "text-ink-soft hover:text-sage-dark after:bg-sage-dark" : "text-white hover:text-white after:bg-white drop-shadow-[0_1px_8px_rgba(20,30,35,0.5)]"
            }`}>
              {blogLabel}
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-3">
          <a data-testid="book-now-header-btn" href={links.janeBook} {...external} className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-sage text-white px-6 py-2.5 text-sm font-semibold transition-[background-color,transform,box-shadow] duration-300 hover:bg-sage-dark hover:-translate-y-0.5 hover:shadow-lg">
            Book Now <ArrowUpRight className="h-4 w-4" />
          </a>
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen((o) => !o)} className={`lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border transition-colors duration-300 ${solid ? "border-sand text-ink" : "border-white/60 text-white"}`} aria-label="Toggle menu">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav data-testid="mobile-nav" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }} className="lg:hidden overflow-hidden backdrop-blur-xl bg-cream/95 border-t border-sand/60">
            <div className="px-6 py-6 flex flex-col gap-1">
              {nav.map((l) => {
                if (l.id === "treatment" && activeAreas.length > 0) {
                  return (
                    <div key={l.id} className="border-b border-sand/50">
                      <button data-testid={`mobile-nav-${l.id}`} onClick={() => goSection(l.id)} className="w-full text-left py-3 font-serif text-2xl text-ink">{l.label}</button>
                      <div className="pb-3 pl-1 flex flex-col gap-1">
                        {activeAreas.map((a) => {
                          const slug = a.slug || slugify(a.name);
                          return (
                            <Link
                              key={a.id || slug}
                              to={`/treatment-areas/${slug}`}
                              onClick={() => setOpen(false)}
                              data-testid={`mobile-nav-ta-${slug}`}
                              className="text-ink-soft text-base py-1.5 hover:text-sage-dark"
                            >
                              — {a.name}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                return (
                  <button key={l.id} data-testid={`mobile-nav-${l.id}`} onClick={() => goSection(l.id)} className="text-left py-3 font-serif text-2xl text-ink border-b border-sand/50">{l.label}</button>
                );
              })}
              <Link data-testid="mobile-nav-blog" to="/blog" onClick={() => setOpen(false)} className={`text-left py-3 font-serif text-2xl text-ink border-b border-sand/50 ${blogEnabled ? "" : "hidden"}`}>{blogLabel}</Link>
              <a data-testid="book-now-mobile-btn" href={links.janeBook} {...external} className="mt-4 inline-flex items-center justify-center gap-1.5 rounded-full bg-sage text-white px-6 py-3.5 font-semibold">
                Book Now <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
